import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../services/dashboard.service';
import { ToastrService } from 'ngx-toastr';
import { formatMarkdown } from 'src/app/_services/markdown.util';

@Component({
  selector: 'tangram-reflect-notes',
  templateUrl: './reflect-notes.component.html',
  styleUrls: ['./reflect-notes.component.scss']
})
export class ReflectNotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pathwayId: string = '';
  @Input() videoId: string = '';
  @Input() isDashboard: boolean = false;
  @Input() currentUser: any = null;
  @Input() iconPath: string = '';
  // Sequential step number supplied by the parent so REFLECT stays gap-free when
  // an earlier section (e.g. EXPLORE) is hidden. Defaults to 2 (its usual slot).
  @Input() stepNumber: number = 2;

  @Output() showLoginAlert = new EventEmitter<void>();

  isExpanded: boolean = false;
  reflectActiveTab: 'private' | 'public' = 'private';
  newPrivateNoteText: string = '';
  newPublicCommentText: string = '';
  personalNotes: any[] = [];
  readonly MAX_NOTE_LENGTH: number = 2000;
  confirmDeleteNoteId: string | null = null;

  // Tracks the in-flight getNotes() so a quick pathway switch (or a parent
  // re-binding inputs in the same tick) can't let an older response
  // overwrite a newer one — or trigger a duplicate fetch.
  private loadNotesSub: Subscription | null = null;

  constructor(
    private dashboardService: DashboardService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.loadNotes();
  }

  // The parent dashboard resolves currentUser (auth) and pathwayId (route /
  // pathway detail) asynchronously, so the @Inputs are usually still empty
  // when ngOnInit fires and the loadNotes() guard bails. Re-fetch whenever
  // those inputs land so previously saved notes actually show up.
  ngOnChanges(changes: SimpleChanges) {
    if (changes['pathwayId'] || changes['videoId'] || changes['currentUser']) {
      this.loadNotes();
    }
  }

  ngOnDestroy() {
    if (this.loadNotesSub) { this.loadNotesSub.unsubscribe(); }
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
  }

  setTab(tab: 'private' | 'public') {
    this.reflectActiveTab = tab;
  }

  loadNotes() {
    if (!this.currentUser || !this.pathwayId) { return; }
    if (this.loadNotesSub) { this.loadNotesSub.unsubscribe(); }
    const videoId = !this.isDashboard && this.videoId ? this.videoId : undefined;
    this.loadNotesSub = this.dashboardService.getNotes(this.pathwayId, videoId).subscribe((response: any) => {
      if (response && response.success) {
        this.personalNotes = response.data || [];
      }
      this.loadNotesSub = null;
    }, () => {
      this.loadNotesSub = null;
    });
  }

  savePrivateNote() {
    this.persistNote(this.newPrivateNoteText, true, () => {
      this.newPrivateNoteText = '';
    });
  }

  postPublicComment() {
    this.persistNote(this.newPublicCommentText, false, () => {
      this.newPublicCommentText = '';
    });
  }

  private persistNote(text: string, isPrivate: boolean, onSuccess: () => void) {
    const trimmed = (text || '').trim();
    if (!trimmed) { return; }
    if (!this.currentUser) {
      this.showLoginAlert.emit();
      return;
    }
    const videoId = !this.isDashboard && this.videoId ? this.videoId : undefined;
    this.dashboardService.createNote(this.pathwayId, trimmed, isPrivate, videoId).subscribe((response: any) => {
      if (response && response.success) {
        this.personalNotes.unshift(response.data);
        onSuccess();
      }
    }, () => {
      this.toastr.error('Failed to save note', 'Error');
    });
  }

  deleteNote(noteId: string) {
    this.confirmDeleteNoteId = noteId;
  }

  confirmDelete() {
    const noteId = this.confirmDeleteNoteId;
    if (!noteId) { return; }
    const note = this.personalNotes.find(n => n._id === noteId);
    if (!note) { this.confirmDeleteNoteId = null; return; }
    const label = !note.isPrivate ? 'comment' : 'note';
    this.dashboardService.deleteNoteApi(noteId).subscribe((response: any) => {
      if (response.success) {
        this.personalNotes = this.personalNotes.filter(n => n._id !== noteId);
      }
      this.confirmDeleteNoteId = null;
    }, () => {
      this.toastr.error('Failed to delete ' + label, 'Error');
      this.confirmDeleteNoteId = null;
    });
  }

  cancelDelete() {
    this.confirmDeleteNoteId = null;
  }

  /** Called by parent via @ViewChild after an AI note is saved. */
  addNote(note: any) {
    if (!note) { return; }
    note.isMarkdown = true;
    if (!note.userId && this.currentUser) {
      note.userId = {
        _id: this.currentUser._id,
        firstName: this.currentUser.firstName,
        lastName: this.currentUser.lastName,
      };
    }
    this.personalNotes = [note, ...this.personalNotes];
    this.isExpanded = true;
  }

  formatNoteBody(text: string): string {
    return formatMarkdown(text);
  }
}
